import React, { useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogProps,
    Grid,
    InternalStandardProps,
    Box,
} from "@mui/material";
import { AppTypography } from "./AppTypography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isAndroid } from "react-device-detect";

const StyledDialog = styled(Dialog)({
    "& .MuiDialog-scrollPaper": {
        alignItems: "flex-start",
    },
    "& .MuiDialog-paperScrollBody": {
        verticalAlign: "top",
    },
});

const DialogWrapper = styled(Box)(({ theme }) => ({
    minHeight: "30vh",
    backgroundColor: `${theme.palette.background.default}`,
    color: `${theme.palette.common.black}`,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    backgroundColor: `${theme.palette.background.default}`,
    color: `${theme.palette.common.black}`,
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    color: theme.palette.common.black,
}));

const FilterIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    fontSize: "22px",
    cursor: "pointer",
    color: theme.palette.grey[600],
}));

const DialogHeaderTxt = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey[600],
}));

export const AppFiltersDialog = (props: AppFiltersDialogProps) => {
    const {
        className,
        onClose,
        showTitle = true,
        titleText,
        children,
        open,
        selectedSpeakers,
        ...rest
    } = props;

    const { dialogHeaderRef } = useFilterDialogHandlers(props);

    return (
        <StyledDialog
            role="dialog"
            aria-modal="true"
            maxWidth={"sm"}
            fullWidth
            className={className}
            scroll="paper"
            open={open}
            {...rest}
        >
            <DialogWrapper>
                {showTitle && (
                    <StyledDialogTitle ref={dialogHeaderRef}>
                        <Grid container>
                            <Grid
                                item
                                xs={12}
                                display={"flex"}
                                justifyContent={"space-between"}
                                alignItems={"center"}
                            >
                                <DialogHeaderTxt
                                    variant="h6"
                                    aria-label={titleText}
                                >
                                    {titleText}
                                </DialogHeaderTxt>
                                <FilterIcon
                                    icon={["fas", "xmark"]}
                                    onClick={onClose}
                                />
                            </Grid>
                        </Grid>
                    </StyledDialogTitle>
                )}
                <StyledDialogContent>
                    <Grid container>
                        <Grid item xs={12}>
                            {children}
                        </Grid>
                    </Grid>
                </StyledDialogContent>
            </DialogWrapper>
        </StyledDialog>
    );
};

const useFilterDialogHandlers = (props: AppFiltersDialogProps) => {
    const dialogHeaderRef = useRef(null);
    const { open, selectedSpeakers } = props;
    useEffect(() => {
        if (open && isAndroid) {
            setTimeout(() => {
                dialogHeaderRef.current?.scrollIntoView({
                    behavior: "smooth",
                });
            }, 100);
        }
    }, [open, isAndroid, selectedSpeakers]);

    return { dialogHeaderRef };
};

export interface AppFiltersDialogProps
    extends InternalStandardProps<DialogProps> {
    onClose: () => void;
    showTitle?: boolean;
    titleText?: string;
    children: React.ReactNode;
    selectedSpeakers?: string[];
}
